/**
 * constants/guest.js — ค่าคงที่ของทะเบียนลูกค้า
 * ใช้ร่วมกันระหว่าง CheckIn.jsx และ GuestRegistry.jsx
 * เพื่อไม่ให้ label/สี ของประเภทผู้เข้าพักเพี้ยนกันคนละหน้า
 */

/* ── ประเภทผู้เข้าพัก — dimension หลักของรายงานวิเคราะห์ ──
   ค่า value ต้องตรงกับ enum ใน backend/models/Booking.js */
export const GUEST_TYPES = [
  { value: "corporate",       th: "พนักงานบริษัท",     en: "Corporate",        color: "#3b82f6" },
  { value: "government",      th: "ราชการ/รัฐวิสาหกิจ", en: "Government",       color: "#8b5cf6" },
  { value: "tourist_th",      th: "นักท่องเที่ยวไทย",   en: "Thai tourist",     color: "#22c55e" },
  { value: "tourist_foreign", th: "นักท่องเที่ยวต่างชาติ", en: "Foreign tourist", color: "#f97316" },
  { value: "local",           th: "คนในพื้นที่",        en: "Local",            color: "#06b6d4" },
  { value: "other",           th: "อื่นๆ",              en: "Other",            color: "#ec4899" },
  { value: "unknown",         th: "ยังไม่ระบุ",         en: "Unspecified",      color: "#cbd5e1" },
];

export const STAY_PURPOSES = [
  { value: "business", th: "ติดต่อธุรกิจ",   en: "Business" },
  { value: "meeting",  th: "ประชุม/สัมมนา",  en: "Meeting / Seminar" },
  { value: "leisure",  th: "ท่องเที่ยว/พักผ่อน", en: "Leisure" },
  { value: "transit",  th: "แวะพักผ่านทาง",  en: "Transit" },
  { value: "event",    th: "งานอีเวนต์",     en: "Event" },
  { value: "other",    th: "อื่นๆ",          en: "Other" },
];

export const guestTypeLabel = (value, lang = "th") => {
  const found = GUEST_TYPES.find((g) => g.value === value);
  if (!found) return value || "-";
  return lang === "en" ? found.en : found.th;
};

export const guestTypeColor = (value) =>
  GUEST_TYPES.find((g) => g.value === value)?.color || "#cbd5e1";

export const stayPurposeLabel = (value, lang = "th") => {
  const found = STAY_PURPOSES.find((p) => p.value === value);
  if (!found) return value || "";
  return lang === "en" ? found.en : found.th;
};

/* ── 77 จังหวัด — ใช้เป็น dropdown จังหวัดทะเบียนรถ และ auto-เติมจากเลขทะเบียน ── */
export const TH_PROVINCES = [
  "กรุงเทพมหานคร", "กระบี่", "กาญจนบุรี", "กาฬสินธุ์", "กำแพงเพชร", "ขอนแก่น",
  "จันทบุรี", "ฉะเชิงเทรา", "ชลบุรี", "ชัยนาท", "ชัยภูมิ", "ชุมพร",
  "เชียงราย", "เชียงใหม่", "ตรัง", "ตราด", "ตาก", "นครนายก",
  "นครปฐม", "นครพนม", "นครราชสีมา", "นครศรีธรรมราช", "นครสวรรค์", "นนทบุรี",
  "นราธิวาส", "น่าน", "บึงกาฬ", "บุรีรัมย์", "ปทุมธานี", "ประจวบคีรีขันธ์",
  "ปราจีนบุรี", "ปัตตานี", "พระนครศรีอยุธยา", "พังงา", "พัทลุง", "พิจิตร",
  "พิษณุโลก", "เพชรบุรี", "เพชรบูรณ์", "แพร่", "พะเยา", "ภูเก็ต",
  "มหาสารคาม", "มุกดาหาร", "แม่ฮ่องสอน", "ยโสธร", "ยะลา", "ร้อยเอ็ด",
  "ระนอง", "ระยอง", "ราชบุรี", "ลพบุรี", "ลำปาง", "ลำพูน",
  "เลย", "ศรีสะเกษ", "สกลนคร", "สงขลา", "สตูล", "สมุทรปราการ",
  "สมุทรสงคราม", "สมุทรสาคร", "สระแก้ว", "สระบุรี", "สิงห์บุรี", "สุโขทัย",
  "สุพรรณบุรี", "สุราษฎร์ธานี", "สุรินทร์", "หนองคาย", "หนองบัวลำภู", "อ่างทอง",
  "อำนาจเจริญ", "อุดรธานี", "อุตรดิตถ์", "อุทัยธานี", "อุบลราชธานี",
];

/* ── ดึงชื่อจังหวัดออกจากเลขทะเบียน เช่น "กข 1234 เชียงใหม่" → "เชียงใหม่" ──
   เรียงจากชื่อยาวไปสั้น กัน "นครพนม" ถูกจับเป็น "นคร..." ตัวอื่นก่อน
   คืน "" ถ้าไม่เจอ — แอดมินเลือกเองจาก dropdown ได้ */
const PROVINCES_BY_LENGTH = [...TH_PROVINCES].sort((a, b) => b.length - a.length);
export function parseCarProvince(plate) {
  const s = String(plate || "");
  return PROVINCES_BY_LENGTH.find((p) => s.includes(p)) || "";
}

/* ── คำที่บ่งบอกหน่วยงานรัฐ — ต้องตรงกับ GOV_KEYWORDS ใน backend/guestRegistry.js ── */
const GOV_KEYWORDS = [
  "เทศบาล", "อบต", "อบจ", "องค์การบริหารส่วน", "กรม", "กระทรวง", "สำนักงาน",
  "การไฟฟ้า", "การประปา", "ศาล", "ที่ว่าการ", "สถานี", "โรงเรียน",
  "มหาวิทยาลัย", "โรงพยาบาล", "สภา", "ตำรวจ", "ทหาร", "ราชการ",
];

const THAI_WORDS = ["ไทย", "ไทยแลนด์", "THAI", "THAILAND", "TH"];
const isThai = (s) => {
  const v = String(s || "").trim();
  return THAI_WORDS.includes(v) || THAI_WORDS.includes(v.toUpperCase());
};

/**
 * เดาประเภทผู้เข้าพักจากข้อมูลที่กรอก — ใช้แสดงค่าแนะนำสดในฟอร์มเช็คอิน
 * backend มีกฎเดียวกันไว้เป็นตัวสำรอง (guestRegistry.suggestGuestType)
 * ถ้าแก้กฎที่นี่ ต้องแก้ที่ backend/guestRegistry.js ด้วย
 */
export function suggestGuestType(info = {}) {
  const company = String(info.companyName || "").trim();
  const taxId   = String(info.companyTaxId || "").trim();

  if (company && GOV_KEYWORDS.some((k) => company.includes(k))) return "government";
  if (company || taxId) return "corporate";

  const nat     = String(info.nationality || "").trim();
  const country = String(info.country || "").trim();
  if (nat && !isThai(nat)) return "tourist_foreign";
  if (!nat && country && !isThai(country)) return "tourist_foreign";

  return "tourist_th";
}
